import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Switch,
  Typography,
  Box,
  Alert,
} from '@mui/material';

interface BillingAutoPaymentModalProps {
  open: boolean;
  onClose: () => void;
  isLoading: boolean;
  autoPaymentEnabled: boolean;
  thresholdAmount: number;
  rechargeAmount: number;
  onSave: (enabled: boolean, threshold: number, recharge: number) => Promise<void>;
}

export function BillingAutoPaymentModal({
  open,
  onClose,
  isLoading,
  autoPaymentEnabled,
  thresholdAmount,
  rechargeAmount,
  onSave,
}: BillingAutoPaymentModalProps): JSX.Element {
  const [enabled, setEnabled] = React.useState(autoPaymentEnabled);
  const [threshold, setThreshold] = React.useState(thresholdAmount);
  const [recharge, setRecharge] = React.useState(rechargeAmount);
  const [error, setError] = React.useState<string>('');

  React.useEffect(() => {
    if (open) {
      setEnabled(autoPaymentEnabled);
      setThreshold(thresholdAmount);
      setRecharge(rechargeAmount);
      setError('');
    }
  }, [open, autoPaymentEnabled, thresholdAmount, rechargeAmount]);

  const handleSave = async () => {
    setError('');

    // Validation
    if (enabled) {
      if (threshold < 0) {
        setError('Threshold amount must be non-negative');
        return;
      }
      if (recharge < 10) {
        setError('Recharge amount must be at least $10');
        return;
      }
    }

    try {
      await onSave(enabled, threshold, recharge);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save auto-payment settings');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Auto-Payment Settings</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1 }}>
          <FormControlLabel
            control={<Switch checked={enabled} onChange={(e) => setEnabled(e.target.checked)} disabled={isLoading} />}
            label="Enable Auto-Payment"
          />
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, ml: 4 }}>
            Automatically add funds when your balance falls below the threshold
          </Typography>

          {enabled && (
            <>
              <TextField
                fullWidth
                label="Threshold Amount"
                type="number"
                value={threshold}
                onChange={(e) => setThreshold(parseFloat(e.target.value) || 0)}
                disabled={isLoading}
                helperText="Trigger auto-payment when balance falls below this amount"
                sx={{ mb: 2 }}
                InputProps={{
                  startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>,
                }}
              />
              <TextField
                fullWidth
                label="Recharge Amount"
                type="number"
                value={recharge}
                onChange={(e) => setRecharge(parseFloat(e.target.value) || 0)}
                disabled={isLoading}
                helperText="Amount to add when auto-payment is triggered (minimum $10)"
                InputProps={{
                  startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>,
                }}
              />
            </>
          )}

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained" disabled={isLoading}>
          {isLoading ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
